// Unit footprint. The set of grid cells a card occupies right now.
// Pure. No React, no Three.js.
//
// Most units sit on one cell. Some occupy several at once: an
// Enchantress-animated aura keeps its aura footprint (a 2-cell
// INTERSECTION_2 border, a 2x2 INTERSECTION_4 block, …) on
// `_auraAnchor` + `_auraShape`, and any future oversized unit can carry
// the same fields. This is the single source of truth those occupancy
// questions go through. Submerge/bury legality, and combat
// attack/defend/intercept reach. So the rule is keyed on OCCUPANCY,
// never on "is this card an aura".

import { anchorCoveredCells } from './auraPlacement';

/**
 * True iff `card` carries an aura footprint. A printed aura (type
 * 'Aura'), or an Enchantress-animated aura (type 'Minion' that kept its
 * stamped `_auraShape`). Plain minions have neither. The single
 * predicate every "treat this like a multi-cell aura" gate goes through
 * (placement halo, anchor re-snap on move).
 */
export function hasAuraFootprint(card) {
  return !!card && (card.type === 'Aura' || !!card._auraShape);
}

/**
 * The grid cells `card` occupies. A unit carrying an aura footprint
 * (`_auraAnchor` + `_auraShape`) returns every covered cell; every
 * other unit returns the single cell its mesh sits on (`singleCell`).
 *
 * @param card        cardInstance
 * @param singleCell  { col, row } the card's mesh cell. The fallback
 *                    for cards without a multi-cell footprint
 * @returns Array<{ col, row }>
 */
export function unitFootprintCells(card, singleCell) {
  const anchor = card?._auraAnchor;
  const shape = card?._auraShape?.shape;
  if (anchor && shape) {
    return anchorCoveredCells(anchor, shape).map((key) => {
      const [col, row] = key.split(',').map(Number);
      return { col, row };
    });
  }
  return singleCell ? [singleCell] : [];
}
